import React, { useCallback, useEffect, useRef } from 'react';
import { Provider } from 'react-redux';
import { Router } from 'react-router-dom';
import { Button, notification as antNotify } from 'antd';
import { store, history, useAppSelector, useAppDispatch } from 'shared/store';
import {
  readAppNotification,
  selectAppNotificationsListPopupable,
  uiWantsModalOpenChanged,
} from 'shared/store/app-notification-slice';
import { AppNotification } from 'types/app-notification';
import { Icon } from 'shared/components/Icon';
import { AppNotificationItem } from '../AppNotificationItem';
import './style.less';

const NAME = 'AppNotificationsSkeletal';
export const AppNotificationsSkeletal: React.FC<{
  containerRef: React.RefObject<HTMLDivElement>;
}> = ({ containerRef }) => {
  const dispatch = useAppDispatch();
  const { uiWantsModalOpen } = useAppSelector(s => s.appNotification);
  // const unreadCount = useAppSelector(selectAppNotificationsUnreadCount);
  const popupable = useAppSelector(selectAppNotificationsListPopupable);
  const shownRef = useRef(new Set<string>());

  const handleReadAll = useCallback(async () => {
    await dispatch(readAppNotification());
    shownRef.current.forEach(anId => antNotify.close(anId));
  }, [dispatch]);

  // Open for unexpireds
  // Note: AntD notify renders outside our app context, so it needs redux/router providers
  // Note: Only pass the id since the content won't update, the item component needs to pull from redux
  useEffect(() => {
    if (!uiWantsModalOpen) {
      popupable.forEach(an => {
        antNotify.open({
          getContainer: () => {
            return containerRef.current || document.body;
          },
          key: an.id.toString(),
          message: (
            <Provider store={store}>
              <Router history={history}>
                <AppNotificationsSkeletalItem anId={an.id} onReadAll={handleReadAll} />
              </Router>
            </Provider>
          ),
          className: NAME,
          duration: (an.popupDuration || 30000) / 1000,
          closeIcon: <></>,
          placement: 'bottomLeft',
          bottom: 14,
          onClose: () => shownRef.current.delete(an.id.toString()),
        });
      });
      shownRef.current = new Set([
        ...shownRef.current,
        ...popupable.map(an => an.id.toString()),
      ]);
    }
  }, [uiWantsModalOpen, popupable, handleReadAll, containerRef]);

  // Close all when modal is open
  useEffect(() => {
    if (uiWantsModalOpen && shownRef.current.size) {
      shownRef.current.forEach(anId => antNotify.close(anId));
      shownRef.current = new Set();
    }
  }, [uiWantsModalOpen]);

  return null;
};

const AppNotificationsSkeletalItem: React.FC<{
  anId: AppNotification['id'];
  onReadAll: () => void;
}> = ({ anId, onReadAll }) => {
  const an = useAppSelector(s => s.appNotification.appNotifications[anId]);
  const dateRead = an?.dateRead;
  const popupExp = an?.isPopupExpired;

  // If the notification is read, close it
  useEffect(() => {
    if (an && (dateRead || popupExp)) {
      antNotify.close(anId.toString());
    }
  }, [an, dateRead, popupExp, anId]);

  const dispatch = useAppDispatch();
  const handleClose = () => {
    antNotify.close(an.id.toString());
    dispatch(readAppNotification(an.id));
  };
  return (
    <>
      <AppNotificationSkeletalHeader onReadAll={onReadAll} />
      <div className={`${NAME}__wrapper`}>
        <div className={`${NAME}__hover-overlay`}>
          <Button
            type="ghost"
            className={`${NAME}__dismiss`}
            icon={<Icon icon="delete" />}
            onClick={handleClose}
          />
        </div>
        <AppNotificationItem
          className={`${NAME}__AppNotificationItem`}
          notification={an}
        />
      </div>
    </>
  );
};

const AppNotificationSkeletalHeader: React.FC<{ onReadAll: () => void }> = ({
  onReadAll,
}) => {
  const dispatch = useAppDispatch();
  const handleViewAll = () => {
    dispatch(uiWantsModalOpenChanged(true));
  };
  return (
    <div className={`${NAME}__header`}>
      <Button className={`${NAME}__header__view-btn`} onClick={handleViewAll}>
        View all <Icon icon="chevronOn" />
      </Button>
      <Button className={`${NAME}__header__read-btn`} onClick={onReadAll}>
        <Icon icon="checkmark" />
        Mark as read
      </Button>
    </div>
  );
};
